import {
  API,
  APP_URLS,
  clientCentralApiStrawberry,
  clientNeoActivite,
  clientNeoActiviteWS,
  clientNeoAuth,
} from "@neonumy/api-clients";
import { Header } from "@neonumy/neonumy-ui";
import * as Sentry from "@sentry/browser";
import { Layout } from "antd";
import axios from "axios";
import { routes } from "config/routes";
import { useFieldsSelection } from "hooks/useFieldsSelection";
import { usePermissions } from "hooks/usePermissions";
import Cookies from "js-cookie";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import Logo from "resources/images/logo.svg?react";
import { validateId } from "utils/validateId";
import useAuthStore from "views/hooks/useAuthStore";
import useCoreStore from "views/hooks/useCoreStore";

interface ApplicationWrapperProps {
  children: React.ReactNode;
}

function ApplicationWrapper(props: ApplicationWrapperProps): React.ReactElement {
  const routeParams: RouteParams = useParams();
  const { canUpdateCompany } = usePermissions();

  const resetStore = useAuthStore(state => state.resetStore);
  const setAccountingFirmSelected = useCoreStore(state => state.setAccountingFirmSelected);

  const companySelected = useCoreStore(state => state.companySelected);
  const accountingFirmSelected = useCoreStore(state => state.accountingFirmSelected);
  const user = useAuthStore(state => state.userData);
  const userIsClient = ["client"].includes(user?.userCategory?.slug ?? "");

  const companyId = routeParams?.companyId;
  const accountingFirmId = routeParams?.accountingFirmId;

  const { getCompany, getAccountingFirm } = useFieldsSelection();

  useEffect(() => {
    if (user) {
      const { companies, accountingFirms } = user;
      const companyID = companyId || (companies && companies.length === 1 && companies[0]);
      const accountingFirmID =
        accountingFirmId || (accountingFirms && accountingFirms.length === 1 && accountingFirms[0]);

      if (companyID && validateId(companyID) && companySelected?.id !== companyID) {
        getCompany({
          variables: {
            id: companyID,
          },
        });
        if (!companyId) {
          routes.navigate(`/company/${companyID}/dashboard`);
        }
      } else if (accountingFirmID && validateId(accountingFirmID) && !companyID) {
        getAccountingFirm({
          variables: {
            accountingFirmId: accountingFirmID,
          },
        });
      }
    } else {
      routes.navigate(`/error`, { replace: true });
    }
  }, [companyId, accountingFirmId]);

  const handleLogout = async () => {
    const resetStores = (): void => {
      clientNeoAuth.resetStore();
      clientCentralApiStrawberry.resetStore();
      resetStore();
      Sentry.setUser(null);
    };

    const resetCookies = (): void => {
      Cookies.remove("access_token");
      Cookies.remove("expireDate");
    };

    await axios(API.revokeToken, {
      method: "post",
      data: `token=${Cookies.get("access_token")}&client_id=${API.clientId}`,
    }).then(() => {
      resetStores();
      resetCookies();
      routes.navigate("/");
    });
  };

  const partialUserData = {
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    email: user?.email ?? "",
    category: user?.userCategory?.slug ?? "",
    isNewraiUser: user?.isNewraiUser ?? false,
  };

  return (
    <Layout>
      <Header
        appLogo={<Logo style={{ height: "30px" }} />}
        appName="NeoFact"
        userData={partialUserData}
        companySelected={companySelected}
        accountingFirmSelected={accountingFirmSelected}
        selectCompanyOptions={{
          isEditable: true,
          companyOnly: userIsClient,
          handleOnSelectCompany: (companyData: unknown) => {
            const company = companyData as CompanySelected;
            routes.navigate(`/company/${company.id}`);
          },
          handleOnSelectAccountingFirm: (accountingFirm: unknown) => {
            setAccountingFirmSelected(accountingFirm as AccountingFirm);
            routes.navigate("/");
          },
        }}
        userAvatar={{
          profileHref: `${APP_URLS.dashboardUrl}account/profile`,
          handleLogout: handleLogout,
        }}
        appsUrlParams={{
          companyId: companySelected?.id,
          accountingFirmId: accountingFirmSelected?.id,
        }}
        notifications={{
          neoActiviteClientHttp: clientNeoActivite,
          neoActiviteClientWS: clientNeoActiviteWS,
        }}
        {...(companyId &&
          canUpdateCompany && {
            dashboardCompanyUrl: `${APP_URLS.dashboardUrl}companies/${companyId}/general/general-info`,
          })}
      />

      {props.children}
    </Layout>
  );
}

export default React.memo(ApplicationWrapper);
